import { useQuery } from '@apollo/client';
import * as React from 'react';

import { NoteKind } from 'shared/models/note';

import { fetchClientsList, fetchPersonDetails } from '../actions';
import { getKeywordSetsHighlightTerms, getSearchHighlightTerms, mergeHighlightTerms } from '../common/searches';
import { Spinner } from '../core-ui/spinner';
import { SEARCH_CONFIG, SearchConfigData } from '../graphql/queries/search';
import { useCandidateScore } from '../hooks/use-candidate-score';
import { useHighlight } from '../hooks/use-highlight';
import { useReduxDispatch, useReduxState } from '../hooks/use-redux';
import { SearchPresetsProvider, useSearchPresets } from '../search/use-search-presets';
import { State } from '../state';
import { PersonProfile } from './person';

interface CandidateDetailProps {
    id: string;
    jobId: string;
    tab?: string;
    menuButton?: JSX.Element;
    jobNotesOnly?: boolean;
    notesKind?: NoteKind;
}

const CandidateDetailsWithSearchPresetsAvailable: React.FC<CandidateDetailProps> = ({
    id,
    jobId,
    tab,
    menuButton,
    jobNotesOnly,
    notesKind
}) => {
    const clients = useReduxState((state: State) => state.clients);
    const jobs = useReduxState((state: State) => state.jobs);
    const candidates = useReduxState((state: State) => state.candidates);
    const dispatch = useReduxDispatch();
    useCandidateScore(id, jobId);
    const { pushHighlightTerms } = useHighlight();
    const { searchPresets } = useSearchPresets();

    const searchId = candidates?.get(jobId)?.get(id)?.searchId;

    const { data: searchConfigData } = useQuery<{ search: SearchConfigData }, { searchId: string }>(SEARCH_CONFIG, {
        skip: !searchId,
        variables: { searchId: searchId ?? '' }
    });

    React.useEffect(() => {
        if (searchConfigData && searchPresets) {
            const searchConfig = searchConfigData.search;
            const tooltip = 'Terms from Search that added this candidate';
            const configTerms = getSearchHighlightTerms(searchPresets, searchConfig.config, tooltip);
            if (searchConfig.keywordSets) {
                const kwTerms = getKeywordSetsHighlightTerms(searchConfig.keywordSets, tooltip);
                return pushHighlightTerms(mergeHighlightTerms(configTerms, kwTerms));
            }
            return pushHighlightTerms(configTerms);
        }
    }, [searchConfigData, searchPresets, pushHighlightTerms]);

    React.useEffect(() => {
        if (!candidates.get(jobId) || !candidates.get(jobId).get(id)) {
            dispatch(fetchPersonDetails(id, jobId));
        }
        if (!clients.list || !clients.initialized) {
            dispatch(fetchClientsList());
        }
    }, [id, jobId, candidates, clients, dispatch]);

    if (
        !jobs.get(jobId) ||
        !candidates.get(jobId) ||
        !candidates.get(jobId).get(id) ||
        !clients ||
        !clients.initialized
    ) {
        return <Spinner />;
    } else {
        const candidate = candidates.get(jobId).get(id);
        const job = jobs.get(candidate.jobId);
        const client = clients.list.get(job.clientId);
        return (
            <PersonProfile
                id={candidate.personId}
                candidate={candidate}
                job={jobs.get(candidate.jobId)}
                client={client}
                tab={tab}
                jobNotesOnly={jobNotesOnly}
                notesKind={notesKind}
                menuButton={menuButton}
            />
        );
    }
};

export const CandidateDetails: React.FC<CandidateDetailProps> = (props) => {
    return (
        <SearchPresetsProvider>
            <CandidateDetailsWithSearchPresetsAvailable {...props} />
        </SearchPresetsProvider>
    );
};
